// Copy for the CAM shortlists tab.
//
// Provider names, tiers and timestamps are data, so they live in the fixtures.
// Everything a person reads is here.

import { type PaywallState } from '@/cam-admin/paywall/Shortlist.fixtures';

export const PAGE = {
  draft: 'Draft',
  // One action where there were two. Publishing the list is what messages the
  // providers, so a draft list has a single button on it and the words that go out
  // are read before it fires.
  publish: 'Publish and send',
  // What is left to do on a published list: chase the silence. Same email, same
  // link, one click.
  resend: 'Resend',
  // The other thing a published list can do, and the only one that ends it. The
  // care got arranged — here, or somewhere we had nothing to do with — and a CAM
  // hearing that on a call is the only way we find out. Named for the act rather
  // than the outcome: "Care secured" is what the list then says, and a button
  // labelled with the answer reads like it is already true.
  markSecured: 'Mark secured',
  // Said in the slot the two buttons were in, because there is nothing left to
  // press. Resend goes with it: chasing providers for care that is already
  // arranged is the one send nobody wants sent.
  //
  // The chip on its own, with no date after it. When it was marked is a fact
  // about the record rather than something a CAM reads off the top of the tab:
  // what they need at a glance is that this list is closed, and a timestamp
  // beside the chip only made them stop and read it.
  secured: 'Care secured',
  // One status column. Communications and status were two models on this row and
  // the 14 Aug spec collapsed them into one, so there is nothing for a second
  // column to say.
  //
  // The time is its own column rather than a caption under the chip. Under the
  // chip it read as part of the status and made every row two lines tall; in a
  // column the dates line up, which is the only way to see that one row has been
  // sitting at messaged since Tuesday. Named in full because the column is
  // answering when, not what: Updated alone reads like a status.
  //
  // Visibility sits next to Status because both are state, and it is a column
  // for the same reason the date is: down a column you can see that one row
  // says Visible while the rest say Hidden, which is what the first provider to
  // pay looks like.
  columns: ['Provider', 'Status', 'Visibility', 'Last updated', ''],
  // The list-level action on a care home list, and the difference is the whole
  // point of the word. Publishing is only the send where there is a paywall to
  // send through: a care home list going live puts the homes in front of the
  // family and writes to nobody, because a CAM already rang them.
  publishOnly: 'Publish',
  // The same columns minus Visibility. With no payment gate there is nothing for
  // it to answer — every home on a published care home list is in front of the
  // family — so the column would read Visible four times down and mean nothing.
  careHomeColumns: ['Provider', 'Status', 'Last updated', ''],
  // A care home list with nobody on it. Same sentence, different word for the
  // thing: a CAM adding to this list is looking for homes.
  careHomeEmpty: 'No homes on this list yet. Add them from Care search.',
  // A list with nobody on it. Only reachable by making one, and providers are
  // added from Care search, so the row says where to go next rather than stating
  // that the table is empty — which the empty table has already said.
  empty: 'Nobody on this list yet. Add providers from Care search.',
} as const;

// The two ways into the shared shortlist modal — the same one Care search opens,
// so a list is created and renamed one way across CAM. Only the two labels that
// sit on this tab live here; the modal owns its own title, fields and buttons.
//
// Create is reached from the foot of the selector rather than from a third button
// on the tab: which list you are looking at and starting another one are the same
// question about the same set. Edit sits beside the selector, because it acts on
// the list that is open.
export const LIST_DIALOG = {
  menuItem: 'Create shortlist',
  editLink: 'Edit',
} as const;

// The one status a row carries. Four words, and a CAM reads them rather than
// setting them: the state moves because the email went out, because Stripe
// confirmed, or because the provider tapped the no.
//
// `isPublished` no longer changes any label: published belongs to the shortlist
// rather than to the row, and the selector already says which state the list is
// in. Kept in the signature because the table and the drawer both call this —
// dropping it would only move the question.
export const statusLabel = (
  state: PaywallState,
  _isPublished: boolean,
): string =>
  ({
    draft: 'Draft',
    messaged: 'Messaged',
    paid: 'Paid',
    declined: 'Declined',
  })[state];

export const ACTIONS = {
  // Still here for a provider added after the list went out. Everyone else was
  // messaged by Publish and send, so Resend is the row action you actually see.
  send: 'Send paywall link',
  resend: 'Resend',
  // The override, on one row: a provider the family cannot see, put in front of
  // them without a payment. Not called Publish — publishing is what happened to
  // the list, and it happened to every row on it at once.
  makeVisible: 'Make visible',
  // Why the link exists, on hover. The rule it states is the one a CAM would
  // otherwise have to be told: waiting for payment is not the only way a provider
  // reaches the family.
  makeVisibleHint: 'You can make a provider visible even if they haven’t paid.',
} as const;

// The row drawer: provider detail, the paywall state in full, and the note.
// `status` rather than `paywall`: the row and the drawer now show one status and
// it is the same one, so calling it something else here would read as a second
// answer to the same question.
export const DRAWER = {
  // Out to the provider's own record in Care search. Named for what it opens
  // rather than for the act of opening it: "View profile" would be the drawer
  // describing a click, and what a CAM wants to know is that the rest of it
  // exists somewhere.
  fullPage: 'See the full provider page',
  phone: 'Phone',
  // The inbox we hold, and where the link went. Not the same fact as the phone
  // number above it: one is who answers, this is what we sent to.
  email: 'Email',
  status: 'Status',
  // Same word as the column, reading the same two values off `PUBLISHED`. The
  // drawer is where a CAM lands from the row they were unsure about, and asking
  // them to close it to re-read whether the family can see this provider is the
  // one question the drawer should never send them back to the table for.
  visibility: 'Visibility',
  linkSent: 'Link sent',
  paid: 'Paid',
  declined: 'Declined',
  // Named contact and their direct line, and that is the lot. Earliest start,
  // minimum package and the complex-needs list all came off: the care assessment
  // holds what the request needs, so asking a provider or a CAM to retype it was
  // collecting a second copy of it against every row.
  contact: 'Named contact',
  // The CAM's own route in, for a provider who rang or replied to the email
  // instead of using the page. Same fields the provider would have tapped, which
  // is the whole idea: one shape, two ways in.
  //
  // The warning is not a disclaimer, it is the rule. We can stand behind what a
  // provider tapped themselves and we cannot stand behind a transcription, so
  // this never reaches the carer, and a CAM typing it needs to know that before
  // they start rather than after.
  camEntry: {
    // The footer control, and one word for both cases. Whether there is anything
    // on the row already is visible right above the button, so a label that
    // switches between Add and Edit is the drawer telling a CAM something they can
    // already see.
    button: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
  },
  // Named for who reads it, not who writes it. A note here can end up in front of
  // the family, and a CAM typing "rang twice, no answer" should know that before
  // they type it rather than after.
  note: 'Note for care circle',
  // What an absent answer says. A CAM reading a row needs to know which half of it
  // is missing, so a blank is a value here rather than a reason to drop the label.
  notGiven: 'Not given',
  notePlaceholder:
    'What was said on the call, what they are waiting on, anything the next person needs to know.',
  done: 'Done',
} as const;

export const PUBLISHED = {
  // The list's own state, and the only place the word Published still appears.
  // Publishing is what sent the links, which every row on the list shares.
  chip: 'Published',
  // The visibility column, and two different words on purpose. Published and
  // Unpublished said this before and were wrong twice over: they belong to the
  // list, and a row that reads Unpublished on a published list is a sentence
  // nobody can parse. Visible and Hidden answer the question the column is
  // actually asking — can the family see this provider.
  visible: 'Visible',
  hidden: 'Hidden',
  // The third answer, on a list that has not gone out. A provider who has paid,
  // or one a CAM has backed, is settled without being visible to anybody yet, and
  // saying Hidden there reads as a decision still to make.
  whenPublished: 'Visible when published',
} as const;

// Publish and send. Publishing is the outbound step now, so this modal is not a
// confirm with a list of providers on it — it is the last read of what we are
// about to say about somebody's mother, to every provider in the area at once.
//
// No tick per provider. At this moment nobody has paid, so there is nobody to hold
// back and nothing for a tick to decide.
// Publishing a care home list. One thing to read, and it is the difference
// between the two kinds of list on this tab: a dom list going live puts nobody in
// front of the family until they pay, and a care home list puts every home there
// at once.
//
// A confirm rather than a form. There is nothing to edit — no message leaves us,
// because a CAM rang the homes days ago — so the modal exists only to say what
// clicking does. That is also why it is short: a CAM who publishes these all day
// should be able to read it in one glance and never read it again.
export const PUBLISH_HOMES = {
  title: 'Publish this shortlist',
  // The fact, then the contrast. The contrast is the reason the modal exists: a
  // CAM moving between the two kinds of list carries the dom rule with them, and
  // the dom rule is that publishing shows nobody.
  body: 'Every home on this list goes in front of the family straight away. There is no payment step on a care home list, so publishing is what they see.',
  // Named for the act, not for agreeing. 'Confirm' would make a CAM check what
  // they are confirming.
  cta: 'Publish',
  cancel: 'Cancel',
} as const;

export const PUBLISH = {
  title: 'Publish and send',
  // Two sentences, and the second is the one that matters. Publishing is the send
  // now, so a CAM clicking it needs to know that going live does not put anybody
  // in front of the family: paying does, and the override is theirs. Said here
  // rather than discovered later on a list of Hidden rows.
  intro:
    'Preview what the providers are going to be sent. Providers will only appear on the shortlist once they have paid unless you mark them as visible.',
  careType: 'Care type',
  // Named for whose needs they are. "Needs" on its own, next to a care type and a
  // funding line, could be the provider's.
  needs: 'Care needs',
  // What the picker shows once it has been used. The chips under it are the list,
  // so the field says how many rather than repeating them.
  needsSummary: (n: number) =>
    n === 0 ? 'Select care needs' : `${n} selected`,
  funding: 'Funding',
  area: 'Area',
  // The one box that is the CAM's own words. Optional, and last, because
  // everything above it came from the assessment and this is the part only the
  // person who took the call knows.
  extra: 'Anything else providers should know',
  extraPlaceholder:
    'Anything a provider would want to know that the assessment does not say.',
  // Reading it as the provider gets it, on its own screen. A CAM who has just
  // edited four fields cannot tell from the fields whether the email reads like a
  // person wrote it.
  preview: 'Preview',
  previewTitle: 'Preview',
  // The email's own footer, named for the provider it is addressed to. The sketch's
  // version names one fixed care home, which in a preview is exactly the wrong
  // detail to leave wrong.
  previewFoot: (provider: string, careType: string) =>
    `You are receiving this because ${provider} covers ${careType} in this area.`,
  previewBack: 'Back to editing',
  // Both halves in the label, because the button does both: the family sees the
  // list and every provider gets a link.
  cta: 'Publish and send',
  cancel: 'Cancel',
} as const;
